import React, { SetStateAction, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { ProductParamInput } from '../hooks/types';
import { RegistPageNumber } from '../hooks/useProductRegistHooks';
import ChapterThree from './InformationRegist/ChapterThree';
import ChapterFive from './InformationRegist/ChapterFive';
import ChapterFour from './InformationRegist/ChapterFour';
import ChapterOne from './InformationRegist/ChapterOne';
import ChapterTwo from './InformationRegist/ChapterTwo';
import ChapterSix from './InformationRegist/ChapterSix';
import ChapterSeven from './InformationRegist/ChapterSeven';
import PreviewProductDetail from './PreviewProductDetail';
import Row from 'components/common/Row';
import { InquiryTemplate } from 'types/api';
import ChapterEight from './InformationRegist/ChapterEight';
import Column from 'components/common/Column';
import { EXCEPT_HEIGHT } from '..';
import { useMutationGenerateContentUpload } from './InformationRegist/graphQL/hooks/useMutationGenerateContentUpload';

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;

  width: 100%;
  max-width: 1200px;
  overflow-y: scroll;
  background-color: #fff;

  ${({ $isMobile }) =>
    !$isMobile &&
    css`
      border-left: 1px solid #eaebec;
      border-right: 1px solid #eaebec;
    `}
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  position: sticky;
  top: 89px;

  width: 100%;
  z-index: 8;
`;

interface InformationRegistIProps {
  isMobile: boolean;
  height: number;
  productParamInput: ProductParamInput;
  registPageNumber: RegistPageNumber;
  imageThumbnail: string[];
  inquiryList: InquiryTemplate;
  setProductParamInput: React.Dispatch<React.SetStateAction<ProductParamInput>>;
  setImageThumbnail: React.Dispatch<SetStateAction<string[]>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  handelGetInquiryTemplate: (id: string) => void;
  handleRefetchInquiryTemplate: (id: string) => void;
}

const InformationRegist: React.FC<InformationRegistIProps> = props => {
  const {
    isMobile,
    height,
    productParamInput,
    registPageNumber,
    imageThumbnail,
    inquiryList,
    setProductParamInput,
    setImageThumbnail,
    handleOnChangeProductParamInput,
    handelGetInquiryTemplate,
    handleRefetchInquiryTemplate,
  } = props;

  const innerHeight = height - EXCEPT_HEIGHT;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [registPageNumber.informationPageNumber.page]);

  const renderInformationRegist = (): JSX.Element => {
    switch (registPageNumber.informationPageNumber.page) {
      case 0: {
        return <ChapterOne isMobile={isMobile} />;
      }
      case 1: {
        return (
          <ChapterTwo
            isMobile={isMobile}
            productParamInput={productParamInput}
            imageThumbnail={imageThumbnail}
            setImageThumbnail={setImageThumbnail}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            handleGenerateContentUpload={handleGenerateContentUpload}
            handleDeleteImage={handleDeleteImage}
          />
        );
      }
      case 2: {
        return (
          <ChapterThree
            isMobile={isMobile}
            productParamInput={productParamInput}
            innerHeight={innerHeight}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
          />
        );
      }
      case 3: {
        return (
          <ChapterFour
            isMobile={isMobile}
            productParamInput={productParamInput}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
          />
        );
      }
      case 4: {
        return (
          <ChapterFive
            isMobile={isMobile}
            productParamInput={productParamInput}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
          />
        );
      }
      case 5: {
        return (
          <ChapterSix
            isMobile={isMobile}
            productParamInput={productParamInput}
            inquiryList={inquiryList}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
            handelGetInquiryTemplate={handelGetInquiryTemplate}
            handleRefetchInquiryTemplate={handleRefetchInquiryTemplate}
          />
        );
      }
      case 6: {
        return (
          <ChapterSeven
            isMobile={isMobile}
            productParamInput={productParamInput}
            handleOnChangeProductParamInput={handleOnChangeProductParamInput}
          />
        );
      }
      case 7: {
        return (
          <ChapterEight
            productParamInput={productParamInput}
            imageThumbnail={imageThumbnail}
            isMobile={isMobile}
          />
        );
      }
      default:
        return <></>;
    }
  };

  const {
    isLoading,
    setIsLoading,
    handleGenerateContentUpload,
    handleDeleteImage,
  } = useMutationGenerateContentUpload({
    productParamInput,
    imageThumbnail,
    setImageThumbnail,
    handleOnChangeProductParamInput,
    isActive: registPageNumber.informationPageNumber.page === 1,
  });

  return (
    <Container
      ref={containerRef}
      $isMobile={isMobile}
      style={{
        justifyContent:
          isMobile && registPageNumber.informationPageNumber.page === 0
            ? 'center'
            : isMobile || registPageNumber.informationPageNumber.page !== 0
            ? 'flex-start'
            : 'center',
        padding: isMobile
          ? registPageNumber.informationPageNumber.page === 0
            ? '64px 20px'
            : registPageNumber.informationPageNumber.page === 7 ||
              registPageNumber.informationPageNumber.page === 2
            ? '0'
            : '0 20px 64px'
          : '0',
        height: `${innerHeight}px`,
      }}
    >
      {isMobile ? (
        renderInformationRegist()
      ) : (
        <Row style={{ width: '100%' }} align="flex-start">
          {registPageNumber.informationPageNumber.page !== 0 ? (
            <ScrollContainer
              ref={containerRef}
              style={{
                padding:
                  registPageNumber.informationPageNumber.page === 7 ||
                  registPageNumber.informationPageNumber.page === 2
                    ? '0'
                    : '0 40px 64px',
                height: `${innerHeight}px`,
                overflowY:
                  registPageNumber.informationPageNumber.page === 2
                    ? 'hidden'
                    : 'scroll',
              }}
            >
              {renderInformationRegist()}
            </ScrollContainer>
          ) : (
            <Column
              style={{
                padding: '32px 20px 64px',
              }}
            >
              {renderInformationRegist()}
            </Column>
          )}
          {registPageNumber.informationPageNumber.page !== 0 &&
            registPageNumber.informationPageNumber.page !== 7 && (
              <Row
                align="flex-start"
                style={{
                  minWidth: '410px',
                  maxWidth: '410px',
                  height: `${innerHeight}px`,
                }}
              >
                <PreviewProductDetail
                  isMobile={isMobile}
                  innerHeight={innerHeight}
                  productParamInput={productParamInput}
                  imageThumbnail={imageThumbnail}
                />
              </Row>
            )}
        </Row>
      )}
    </Container>
  );
};

export default InformationRegist;
